import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Separator } from "./ui/separator";
import {
  FileText,
  Send,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  MessageSquare,
  Calendar,
  Search,
  Filter,
  PlusCircle,
  ArrowRight,
  Lightbulb,
  TrendingUp,
  DollarSign,
  Target,
  Users,
  Upload,
  Paperclip,
  Download,
  Briefcase,
  LineChart,
  Shield,
  UserCheck,
  PieChart,
  BarChart3,
} from "lucide-react";
import { Checkbox } from "./ui/checkbox";
import { FileUploadSection } from "./FileUploadSection";
import { toast } from "sonner";
import { uploadService } from "@/services/uploadService";
import { ResearchDocument, ResearchAdvisingRequest } from "@/interface";
import { useAuth } from "../hooks/useAuth";
import { ScheduleMeetingDialog } from "./ScheduleMeetingDialog";
import { analystService } from "@/services/analystService";
import { researchAdvisingService } from "@/services/researchAdvisingService";
import { meetingService } from "@/services/meetingService";
import { cn } from "./ui/utils";
import { FINANCIAL_SPECIALIZATIONS, ANALYST_FEE_TYPES } from "../utils/financialTaxonomy";
import { ServiceProvider } from "@/interface/ServiceProvider";

interface ResearchRequest {
  id: string;
  title: string;
  requestType: string;
  description: string;
  context: string;
  requester: {
    id: string;
    name: string;
    role: "investor" | "advisor";
    firm?: string;
  };
  analyst?: {
    id: string;
    name: string;
    firm: string;
  };
  status: "pending" | "accepted" | "in_progress" | "completed" | "declined";
  priority: "low" | "medium" | "high" | "urgent";
  requestedDate: string;
  dueDate?: string;
  completedDate?: string;
  focusAreas: string[];
  specificQuestions?: string[];
  portfolioContext?: {
    totalValue?: string;
    topHoldings?: string[];
    concerns?: string[];
  };
  messages?: Message[];
  deliverables?: string[];
  documents?: ResearchDocument[];
  researchContent?: string;
  budget?: string;
}

interface Message {
  id: string;
  sender: "requester" | "analyst";
  senderName: string;
  content: string;
  timestamp: string;
}

const sampleRequests: ResearchRequest[] = [
  {
    id: "req1",
    title: "Technology Sector Portfolio Review",
    requestType: "portfolio_review",
    description: "Need analysis of my tech-heavy portfolio. Concerned about concentration risk and looking for rebalancing recommendations.",
    context: "My portfolio is 65% technology stocks, primarily large-cap growth. I'm worried about valuations and want to understand if I should take profits or stay the course.",
    requester: {
      id: "inv1",
      name: "Sarah Johnson",
      role: "investor",
    },
    analyst: {
      id: "ana1",
      name: "Sarah Chen",
      firm: "Chen Analytics",
    },
    status: "in_progress",
    priority: "high",
    requestedDate: "2025-10-14 10:30 AM",
    dueDate: "2025-10-21",
    focusAreas: ["Technology", "Risk Management", "Portfolio Rebalancing"],
    specificQuestions: [
      "Should I reduce my NVDA position (currently 12% of portfolio)?",
      "What are good defensive alternatives in tech?",
      "How does my tech exposure compare to benchmarks?",
    ],
    portfolioContext: {
      totalValue: "$850K",
      topHoldings: ["NVDA", "MSFT", "AAPL", "GOOGL", "AMZN"],
      concerns: ["Concentration risk", "Overvaluation", "Interest rate sensitivity"],
    },
    messages: [
      {
        id: "m1",
        sender: "requester",
        senderName: "Sarah Johnson",
        content: "Hi Sarah, I'm looking for a comprehensive review of my tech portfolio. I've been hearing a lot about concentration risk and wondering if I should diversify.",
        timestamp: "2025-10-14 10:30 AM",
      },
      {
        id: "m2",
        sender: "analyst",
        senderName: "Sarah Chen",
        content: "Thanks for reaching out! I'd be happy to help. Can you share your full holdings list and current allocation percentages? Also, what's your investment timeframe and risk tolerance?",
        timestamp: "2025-10-14 11:15 AM",
      },
      {
        id: "m3",
        sender: "requester",
        senderName: "Sarah Johnson",
        content: "Sure! I have a 10+ year timeframe and moderate-to-high risk tolerance. I'll send over my holdings list via secure file share.",
        timestamp: "2025-10-14 02:45 PM",
      },
      {
        id: "m4",
        sender: "analyst",
        senderName: "Sarah Chen",
        content: "Perfect. I'm diving into the analysis now. I'll have a comprehensive report ready by Friday. Initial observation: your tech allocation is indeed high relative to market cap weight (32%), but fundamentals are solid.",
        timestamp: "2025-10-15 09:20 AM",
      },
    ],
    deliverables: [
      "Portfolio analysis report",
      "Risk assessment",
      "Rebalancing recommendations",
      "Alternative investment ideas",
    ],
  },
  {
    id: "req2",
    title: "ESG Investment Strategy Research",
    requestType: "custom",
    description: "Seeking research on authentic ESG investment opportunities, particularly in renewable energy and sustainable technology sectors.",
    context: "My clients are increasingly interested in ESG investing, but I want to ensure we're recommending companies with genuine sustainability practices, not just greenwashing.",
    requester: {
      id: "adv1",
      name: "Michael Torres",
      role: "advisor",
      firm: "Torres Wealth Management",
    },
    analyst: {
      id: "ana3",
      name: "Maya Patel",
      firm: "Patel ESG Research",
    },
    status: "accepted",
    priority: "medium",
    requestedDate: "2025-10-13 02:15 PM",
    dueDate: "2025-10-27",
    focusAreas: ["ESG", "Renewable Energy", "Sustainable Technology", "Impact Investing"],
    specificQuestions: [
      "How to identify greenwashing vs. authentic ESG leaders?",
      "What metrics should we use to evaluate ESG performance?",
      "Which renewable energy subsectors have best risk/reward?",
      "Are ESG ETFs better than individual stock selection?",
    ],
    portfolioContext: {
      totalValue: "$15M AUM",
      concerns: ["Greenwashing", "Performance vs. traditional portfolios", "Regulatory changes"],
    },
    messages: [
      {
        id: "m1",
        sender: "requester",
        senderName: "Michael Torres",
        content: "Hi Maya, I've been following your ESG research and would love to work with you on developing an ESG investment framework for my practice.",
        timestamp: "2025-10-13 02:15 PM",
      },
      {
        id: "m2",
        sender: "analyst",
        senderName: "Maya Patel",
        content: "I'd be delighted to help! This is right in my wheelhouse. Let me prepare a comprehensive framework covering screening criteria, performance metrics, and specific investment ideas. Expected delivery in 2 weeks.",
        timestamp: "2025-10-13 04:30 PM",
      },
    ],
    deliverables: [
      "ESG screening framework",
      "Top 10 ESG investment ideas",
      "Performance comparison analysis",
      "Client presentation materials",
    ],
    budget: "$2,500",
  },
  {
    id: "req3",
    title: "Healthcare Stock Analysis: UNH vs. CVS",
    requestType: "stock_research",
    description: "Need detailed comparison of UnitedHealth (UNH) and CVS Health for potential investment decision.",
    context: "Looking to add healthcare exposure to my portfolio. Interested in managed care/pharmacy benefit space but unsure which company offers better value.",
    requester: {
      id: "inv2",
      name: "Michael Chen",
      role: "investor",
    },
    status: "pending",
    priority: "medium",
    requestedDate: "2025-10-15 09:45 AM",
    focusAreas: ["Healthcare", "Stock Analysis", "Valuation"],
    specificQuestions: [
      "Which company has better growth prospects?",
      "How do margins compare?",
      "What are the regulatory risks for each?",
      "Which is better valued currently?",
    ],
    deliverables: [
      "Comparative financial analysis",
      "Valuation models",
      "Risk assessment",
      "Buy/hold/sell recommendation",
    ],
  },
  {
    id: "req4",
    title: "Q4 Market Outlook & Asset Allocation",
    requestType: "market_outlook",
    description: "Quarterly market outlook to inform asset allocation decisions for client portfolios.",
    context: "End of year approaching and need comprehensive market outlook covering all major asset classes to guide rebalancing decisions.",
    requester: {
      id: "adv2",
      name: "Jennifer Martinez",
      role: "advisor",
      firm: "Martinez Financial Advisors",
    },
    analyst: {
      id: "ana2",
      name: "Alex Thompson",
      firm: "Thompson Research Group",
    },
    status: "completed",
    priority: "high",
    requestedDate: "2025-09-28 11:00 AM",
    dueDate: "2025-10-05",
    completedDate: "2025-10-04 03:30 PM",
    focusAreas: ["Market Outlook", "Asset Allocation", "Economic Forecast"],
    specificQuestions: [
      "What's your GDP and inflation forecast?",
      "How should we position for potential Fed rate cuts?",
      "Best sectors for Q4?",
      "International vs. domestic equity allocation?",
    ],
    portfolioContext: {
      totalValue: "$25M AUM",
      topHoldings: ["Diversified across asset classes"],
      concerns: ["Interest rate uncertainty", "Election year volatility"],
    },
    deliverables: [
      "Market outlook report",
      "Asset allocation recommendations",
      "Sector preferences",
      "Risk scenario analysis",
    ],
    budget: "$3,500",
  },
  {
    id: "req5",
    title: "Small-Cap Value Opportunities",
    requestType: "sector_analysis",
    description: "Research on small-cap value sector for potential portfolio addition.",
    context: "Portfolio is heavy in large-cap growth. Looking to diversify with small-cap value exposure.",
    requester: {
      id: "inv3",
      name: "David Lee",
      role: "investor",
    },
    status: "declined",
    priority: "low",
    requestedDate: "2025-10-10 04:20 PM",
    focusAreas: ["Small-Cap", "Value Investing"],
    messages: [
      {
        id: "m1",
        sender: "requester",
        senderName: "David Lee",
        content: "Looking for small-cap value research. Can you help?",
        timestamp: "2025-10-10 04:20 PM",
      },
      {
        id: "m2",
        sender: "analyst",
        senderName: "Sarah Chen",
        content: "Thanks for the interest, but small-cap is outside my coverage area. I specialize in large-cap technology. I'd recommend reaching out to Alex Thompson who covers small-cap extensively.",
        timestamp: "2025-10-11 09:30 AM",
      },
    ],
  },
];

interface ResearchRequestsProps {
  userRole: "investor" | "advisor" | "analyst";
  userId?: string;
  initialRequests?: any[];
  isLoading?: boolean;
  onRefresh?: () => void;
}

export function ResearchRequests({ userRole: roleProp, userId, initialRequests, isLoading, onRefresh }: ResearchRequestsProps) {
  const { userRole: authRole } = useAuth();
  const userRole = (roleProp || authRole || localStorage.getItem('userRole') || "investor").toLowerCase();
  const currentUserId = userId || localStorage.getItem('userId') || "user1";
  const [requests, setRequests] = useState<ResearchRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<ResearchRequest | null>(null);
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [isFetchingRequests, setIsFetchingRequests] = useState(false);

  // Map backend data to frontend interface
  const mapRequests = (data: any[]): ResearchRequest[] => {
    return data.map((req: any) => ({
      id: req.id,
      title: req.title,
      requestType: req.requestType,
      description: req.description,
      context: req.situationContext || "",
      requester: {
        id: req.requesterId,
        name: req.requesterName,
        role: (req.requesterRole?.toLowerCase() === "investor" ? "investor" : "advisor") as "investor" | "advisor",
        firm: req.requesterFirm,
      },
      analyst: req.categoryType === "RESEARCH" ? {
        id: req.recipientId,
        name: req.recipientName,
        firm: req.recipientFirm || "WealthBrainAI",
      } : undefined,
      status: (req.status?.toLowerCase() || "pending") as any,
      priority: (req.priority || "medium") as any,
      requestedDate: req.requestedDate || req.createdAt,
      dueDate: req.dueDate,
      completedDate: req.completedDate,
      focusAreas: Array.isArray(req.focusAreas) ? req.focusAreas : 
                  (Array.isArray(req.primaryGoals) ? req.primaryGoals.map((g: any) => g.value || g.label) : []),
      specificQuestions: typeof req.questions === 'string' 
        ? req.questions.split('\n').filter((q: string) => q.trim() !== '') 
        : (Array.isArray(req.questions) ? req.questions : []),
      deliverables: typeof req.deliverables === 'string'
        ? req.deliverables.split('\n').filter((d: string) => d.trim() !== '')
        : (Array.isArray(req.deliverables) ? req.deliverables : []),
      researchContent: req.researchContent,
      portfolioContext: req.portfolioContext || (req.portfolioValue ? {
        totalValue: req.portfolioValue,
        topHoldings: [],
        concerns: [],
      } : undefined),
      messages: Array.isArray(req.messages) ? req.messages : [],
      documents: Array.isArray(req.documents) ? req.documents : [],
      budget: req.feeStructure?.amount ? `$${req.feeStructure.amount}` : undefined,
    }));
  };

  // Effect to handle initialRequests if provided
  useEffect(() => {
    if (initialRequests && Array.isArray(initialRequests)) {
      setRequests(mapRequests(initialRequests));
    }
  }, [initialRequests]);

  // Effect to show loading state if passed from parent
  useEffect(() => {
    if (isLoading !== undefined) {
      setIsFetchingRequests(isLoading);
    }
  }, [isLoading]);

  // Fetch all requests for the provider (Analyst or Advisor) if not provided by parent
  useEffect(() => {
    const fetchRequests = async () => {
      if (!currentUserId || initialRequests) return;

      try {
        setIsFetchingRequests(true);
        const response = await researchAdvisingService.getRequests(currentUserId);
        const data = response.data || response;
        
        if (Array.isArray(data)) {
          setRequests(mapRequests(data));
        }
      } catch (error) {
        console.error("Error fetching requests:", error);
        toast.error("Failed to load requests");
      } finally {
        setIsFetchingRequests(false);
      }
    };

    fetchRequests();
  }, [currentUserId, initialRequests]);

  const [sharePortfolio, setSharePortfolio] = useState(false);
  const [shareProfile, setShareProfile] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [deliverables, setDeliverables] = useState("");
  const [requestTitle, setRequestTitle] = useState("");
  const [requestPriority, setRequestPriority] = useState("medium");
  const [requestDescription, setRequestDescription] = useState("");
  const [requestSituation, setRequestSituation] = useState("");
  const [questions, setQuestions] = useState("");
  const [totalValue, setTotalValue] = useState("");
  const [topHoldings, setTopHoldings] = useState("");
  const [concerns, setConcerns] = useState("");
  const [researchType, setResearchType] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [selectedAnalystId, setSelectedAnalystId] = useState<string>("");
  const [researchFeeType, setResearchFeeType] = useState<string>("");
  const [researchFeeAmount, setResearchFeeAmount] = useState<string>("");
  const [researchPaymentTerm, setResearchPaymentTerm] = useState<string>("");
  const [availableAnalysts, setAvailableAnalysts] = useState<ServiceProvider[]>([]);
  const [isFetchingAnalysts, setIsFetchingAnalysts] = useState(false);
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [researchContentState, setResearchContentState] = useState("");
  const [isUploadingAdditional, setIsUploadingAdditional] = useState(false);
  const [isAdditionalUploadOpen, setIsAdditionalUploadOpen] = useState(false);
  const [upcomingMeetings, setUpcomingMeetings] = useState<any[]>([]);

  const fetchUpcomingMeetings = async () => {
    if (!selectedRequest || !currentUserId) return;
    
    const participantId = userRole === "analyst" 
      ? selectedRequest.requester.id 
      : selectedRequest.analyst?.id;
      
    if (!participantId) return;

    try {
      // Fetch both PENDING and ACCEPTED meetings
      const [pendingRes, acceptedRes] = await Promise.all([
        meetingService.getMyMeetingsWithParticipant(currentUserId, participantId, "PENDING", false),
        meetingService.getMyMeetingsWithParticipant(currentUserId, participantId, "ACCEPTED", false)
      ]);
      
      const pMeetings = pendingRes.data || (Array.isArray(pendingRes) ? pendingRes : []);
      const aMeetings = acceptedRes.data || (Array.isArray(acceptedRes) ? acceptedRes : []);
      
      setUpcomingMeetings([...pMeetings, ...aMeetings].sort((a, b) => 
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      ));
    } catch (error) {
      console.error("Error fetching upcoming meetings:", error);
    }
  };

  useEffect(() => {
    fetchUpcomingMeetings();
  }, [selectedRequest, currentUserId, userRole]);

  const handleUpdateMeetingStatus = async (meetingId: string, userId: any, status: string) => {
    if (!currentUserId) return;

    try {
      await meetingService.updateMeetingStatus(meetingId, userId, status);
      toast.success(`Meeting ${status.toLowerCase()}ed successfully`);
      fetchUpcomingMeetings();
    } catch (error) {
      console.error("Error updating meeting status:", error);
      toast.error(`Failed to ${status.toLowerCase()} meeting`);
    }
  };

  useEffect(() => {
    const fetchAnalysts = async () => {
      if (!isRequestDialogOpen || userRole === "analyst") return;
      
      const userId = currentUserId;
      if (!userId) return;

      try {
        setIsFetchingAnalysts(true);
        const response = await analystService.getConnectedOrEngagedAnalysts(userId);
        const data = response.data || response;
        if (Array.isArray(data)) {
          setAvailableAnalysts(data);
        }
      } catch (error) {
        console.error("Error fetching analysts:", error);
        toast.error("Failed to load analysts");
      } finally {
        setIsFetchingAnalysts(false);
      }
    };

    fetchAnalysts();
  }, [isRequestDialogOpen, userRole, currentUserId]);

  useEffect(() => {
    if (!isRequestDialogOpen) {
      setSelectedAnalystId("");
      setRequestTitle("");
      setRequestPriority("medium");
      setRequestDescription("");
      setRequestSituation("");
      setQuestions("");
      setDeliverables("");
      setTotalValue("");
      setTopHoldings("");
      setConcerns("");
      setResearchFeeType("");
      setResearchFeeAmount("");
      setResearchPaymentTerm("");
      setResearchType("");
      setSelectedGoals([]);
      setUploadedFiles([]);
      setSharePortfolio(false);
      setShareProfile(false);
    }
  }, [isRequestDialogOpen]);

  useEffect(() => {
    if (selectedRequest) {
      setResearchContentState(selectedRequest.researchContent || "");
    }
  }, [selectedRequest]);

  const handleCreateRequest = async () => {
    const userId = localStorage.getItem("userId");
    const userName = localStorage.getItem("userName") || "Client";
    const userRoleValue = localStorage.getItem("userRole") || "INVESTOR";

    if (!userId) {
      toast.error("Please log in to submit a request");
      return;
    }

    if (!selectedAnalystId) {
      toast.error("Please select an analyst");
      return;
    }

    if (!researchType) {
      toast.error("Please select a research type");
      return;
    }

    if (!requestTitle.trim()) {
      toast.error("Please provide a title for your request");
      return;
    }

    try {
      setIsFetchingRequests(true);
      const selectedAnalyst = availableAnalysts.find(a => a.id === selectedAnalystId);

      // 1. Upload files first if any
      const uploadedDocuments: ResearchDocument[] = [];
      if (uploadedFiles.length > 0) {
        for (const file of uploadedFiles) {
          try {
            const uploadResult = await uploadService.uploadFile(file, "research-requests");
            uploadedDocuments.push({
              requestId: "", // Will be associated by backend
              uploadedByUserId: userId,
              uploadedByName: userName,
              documentName: file.name,
              documentType: "REQUEST_ATTACHMENT",
              fileUrl: uploadResult.url,
              fileSizeBytes: file.size,
              fileMimeType: file.type,
              isSensitive: false,
              uploadedAt: new Date().toISOString(),
              description: `Attachment for research request: ${requestTitle}`
            } as any);
          } catch (uploadErr) {
            console.error(`Failed to upload file ${file.name}:`, uploadErr);
            throw new Error(`Failed to upload ${file.name}`);
          }
        }
      }

      const feeStructure = {
        pricingInfo: {
          type: researchFeeType || "FIXED",
          range: "As agreed",
          details: "Standard research request fee"
        },
        amount: parseFloat(researchFeeAmount) || 0,
        frequency: researchPaymentTerm || "ONE_TIME"
      };

      const requestDto: ResearchAdvisingRequest = {
        title: requestTitle,
        requestType: researchType,
        categoryType: "RESEARCH",
        description: requestDescription,
        requesterId: userId,
        requesterName: userName,
        requesterRole: userRoleValue,
        recipientId: selectedAnalystId,
        recipientName: selectedAnalyst?.name || "",
        recipientRole: "ANALYST",
        status: "pending",
        priority: requestPriority,
        requestedDate: new Date().toISOString(),
        dueDate: null,
        acceptedDate: null,
        completedDate: null,
        focusAreas: selectedGoals,
        situationContext: requestSituation,
        portfolioContext: {
          totalValue,
          topHoldings: topHoldings.split(',').map(s => s.trim()).filter(s => s !== ""),
          concerns: concerns.split(',').map(s => s.trim()).filter(s => s !== "")
        },
        questions: questions,
        deliverables: deliverables,
        hasCurrentAdvisor: false,
        engagementSigned: false,
        engagementSignedAt: null,
        sharePortfolio,
        shareProfile,
        documents: uploadedDocuments,
        feeStructure,
        primaryGoals: selectedGoals.map(goal => ({ label: goal, value: goal })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await researchAdvisingService.createRequest(requestDto);
      
      toast.success("Research request sent successfully!");
      setIsRequestDialogOpen(false);
      
      if (onRefresh) {
        onRefresh();
      } else {
        // Fallback if no refresh function provided
        const response = await researchAdvisingService.getRequests(currentUserId);
        const data = response.data || response;
        if (Array.isArray(data)) {
          setRequests(mapRequests(data));
        }
      }
    } catch (error: any) {
      console.error('Error creating request:', error);
      toast.error(error.message || "Failed to submit request");
    } finally {
      setIsFetchingRequests(false);
    }
  };

  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedRequest) return;

    const message: Message = {
      id: `m${Date.now()}`,
      sender: userRole === "analyst" ? "analyst" : "requester",
      senderName: localStorage.getItem("userName") || (userRole === "analyst" ? "Analyst" : "Client"),
      content: newMessage,
      timestamp: new Date().toLocaleString(),
    };

    const updatedRequest = {
      ...selectedRequest,
      messages: [...(selectedRequest.messages || []), message],
    };

    setRequests(requests.map((r) => (r.id === selectedRequest.id ? updatedRequest : r)));
    setSelectedRequest(updatedRequest);
    setNewMessage("");
    toast.success("Message sent");
  };

  const handleDownload = async (fileUrl: string) => {
    try {
      await uploadService.downloadFile(fileUrl);
    } catch (error: any) {
      toast.error(error.message || "Failed to download file");
    }
  };

  const handleUploadAdditionalFiles = async (files: File[]) => {
    if (!selectedRequest || files.length === 0) return;
    
    const userId = localStorage.getItem("userId");
    const userName = localStorage.getItem("userName") || "User";

    try {
      setIsUploadingAdditional(true);
      const newDocuments: ResearchDocument[] = [];
      
      for (const file of files) {
        const uploadResult = await uploadService.uploadFile(file, "research-requests");
        newDocuments.push({
          requestId: selectedRequest.id,
          uploadedByUserId: userId || "",
          uploadedByName: userName,
          documentName: file.name,
          documentType: "ADDITIONAL_ATTACHMENT",
          fileUrl: uploadResult.url,
          fileSizeBytes: file.size,
          fileMimeType: file.type,
          isSensitive: false,
          uploadedAt: new Date().toISOString(),
          description: `Additional attachment for request: ${selectedRequest.title}`
        } as any);
      }

      await researchAdvisingService.addDocuments(selectedRequest.id, newDocuments);
      toast.success("Additional files uploaded successfully");
      setIsAdditionalUploadOpen(false);
      
      // Refresh request
      const response = await researchAdvisingService.getRequests(currentUserId);
      const data = response.data || response;
      if (Array.isArray(data)) {
        const updatedRequests = mapRequests(data);
        setRequests(updatedRequests);
        const updated = updatedRequests.find(r => r.id === selectedRequest.id);
        if (updated) setSelectedRequest(updated);
      }
    } catch (error: any) {
      console.error("Error uploading additional files:", error);
      toast.error(error.message || "Failed to upload additional files");
    } finally {
      setIsUploadingAdditional(false);
    }
  };

  const handleSaveResearchContent = async () => {
    if (!selectedRequest) return;

    try {
      await researchAdvisingService.updateResearchContent(selectedRequest.id, researchContentState);
      toast.success("Research content saved successfully");
      
      // Refresh requests list
      const response = await researchAdvisingService.getRequests(currentUserId);
      const data = response.data || response;
      if (Array.isArray(data)) {
        const updatedRequests = mapRequests(data);
        setRequests(updatedRequests);
        const updated = updatedRequests.find(r => r.id === selectedRequest.id);
        if (updated) setSelectedRequest(updated);
      }
    } catch (error: any) {
      console.error("Error saving research content:", error);
      toast.error(error.message || "Failed to save research content");
    }
  };

  const handleUpdateStatus = async (requestId: string, newStatus: ResearchRequest["status"]) => {
    try {
      // Update status in backend
      await researchAdvisingService.updateRequestStatus(requestId, newStatus.toUpperCase());
      
      const updatedRequests = requests.map((req) =>
        req.id === requestId ? { ...req, status: newStatus.toLowerCase() as any } : req
      );
      setRequests(updatedRequests);
      if (selectedRequest?.id === requestId) {
        setSelectedRequest(updatedRequests.find((r) => r.id === requestId) || null);
      }
      toast.success(`Request ${newStatus.replace("_", " ")}`);
    } catch (error) {
      console.error("Error updating request status:", error);
      toast.error("Failed to update status");
    }
  };

  const getStatusColor = (status: ResearchRequest["status"]) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300";
      case "accepted":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
      case "in_progress":
        return "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300";
      case "completed":
        return "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300";
      case "declined":
        return "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300";
      case "high":
        return "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300";
      case "medium":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300";
      case "low":
        return "bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getRequestTypeIcon = (type: string) => {
    switch (type) {
      // Research Types
      case "sector_analysis":
        return TrendingUp;
      case "stock_research":
        return Target;
      case "portfolio_review":
        return PieChart;
      case "market_outlook":
        return BarChart3;
      case "custom":
        return Lightbulb;
      
      // Advising Types
      case "comprehensive_financial_planning":
        return FileText;
      case "investment_portfolio_management":
        return LineChart;
      case "retirement_income_planning":
        return Calendar;
      case "tax_preparation_strategy":
        return DollarSign;
      case "estate_planning_services":
        return Shield;
      case "insurance_analysis_planning":
        return UserCheck;
      case "business_financial_planning":
        return Briefcase;
      
      default:
        return FileText; // Default icon to prevent crash
    }
  };

  const getRequestTypeLabel = (type: string) => {
    switch (type) {
      // Research Types
      case "sector_analysis":
        return "Sector Analysis";
      case "stock_research":
        return "Stock Research";
      case "portfolio_review":
        return "Portfolio Review";
      case "market_outlook":
        return "Market Outlook";
      case "custom":
        return "Custom Request";
      
      // Advising Types
      case "comprehensive_financial_planning":
        return "Comprehensive Financial Planning";
      case "investment_portfolio_management":
        return "Investment Portfolio Management";
      case "retirement_income_planning":
        return "Retirement Income Planning";
      case "tax_preparation_strategy":
        return "Tax Preparation & Strategy";
      case "estate_planning_services":
        return "Estate Planning Services";
      case "insurance_analysis_planning":
        return "Insurance Analysis & Planning";
      case "business_financial_planning":
        return "Business Financial Planning";
      
      default:
        return type?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) || "Request";
    }
  };

  // Filter requests based on user role
  const filteredRequests = requests.filter((req) => {
    // Status filter
    if (filterStatus !== "all" && req.status !== filterStatus) return false;

    // Search filter
    if (
      searchQuery &&
      !req.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !req.description.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }

    // Role-based filtering
    if (userRole === "analyst") {
      return true; // Analysts see all requests sent to them
    } else {
      return req.requester.id == currentUserId; // Clients see their own requests
    }
  });

  const pendingCount = requests.filter((r) => r.status === "pending").length;
  const activeCount = requests.filter(
    (r) => r.status === "accepted" || r.status === "in_progress"
  ).length;
  const completedCount = requests.filter((r) => r.status === "completed").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl">
            {userRole === "analyst" ? "Research Requests" : "My Research Requests"}
          </h2>
          <p className="text-muted-foreground">
            {userRole === "analyst"
              ? "Manage incoming research requests from your clients"
              : "Request custom research and analysis from your analysts"}
          </p>
        </div>
        {userRole !== "analyst" && (
          <Dialog open={isRequestDialogOpen} onOpenChange={setIsRequestDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <PlusCircle className="mr-2 h-4 w-4" />
                New Research Request
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Request Custom Research</DialogTitle>
                <DialogDescription>
                  Submit a research request to your analyst. They'll review and provide a custom analysis.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Select Analyst</Label>
                  <Select value={selectedAnalystId} onValueChange={setSelectedAnalystId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose an analyst">
                        {selectedAnalystId ? availableAnalysts.find(a => a.id === selectedAnalystId)?.name : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {isFetchingAnalysts ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          <Clock className="h-4 w-4 animate-spin inline mr-2" />
                          Loading analysts...
                        </div>
                      ) : availableAnalysts.length > 0 ? (
                        availableAnalysts.map((analyst) => (
                          <SelectItem key={analyst.id} value={analyst.id}>
                            <div>
                              <p className="font-medium flex items-center">
                                {analyst.name}
                                {analyst.isConnected && (
                                  <span className="ml-2 text-[10px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded border border-green-100 uppercase tracking-wider">
                                    Connected
                                  </span>
                                )}
                                {analyst.isEngaged && (
                                  <span className="ml-2 text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 uppercase tracking-wider">
                                    Engaged
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {analyst.firm} • {analyst.specialties?.join(", ")}
                              </p>
                            </div>
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          No analysts found.
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Request Type</Label>
                  <Select value={researchType} onValueChange={setResearchType}>
                    <SelectTrigger>
                      <SelectValue placeholder="What type of research do you need?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sector_analysis">
                        Sector Analysis - Industry trends and opportunities
                      </SelectItem>
                      <SelectItem value="stock_research">
                        Stock Research - Specific company deep dive
                      </SelectItem>
                      <SelectItem value="portfolio_review">
                        Portfolio Review - Comprehensive portfolio analysis
                      </SelectItem>
                      <SelectItem value="market_outlook">
                        Market Outlook - Economic and market forecasts
                      </SelectItem>
                      <SelectItem value="custom">
                        Custom Request - Other research needs
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Request Title</Label>
                  <Input 
                    placeholder="e.g., Technology Sector Portfolio Review" 
                    value={requestTitle}
                    onChange={(e) => setRequestTitle(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    placeholder="Briefly describe what you're looking for..."
                    rows={3}
                    value={requestDescription}
                    onChange={(e) => setRequestDescription(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Context & Background</Label>
                  <Textarea
                    placeholder="Provide context about your situation, portfolio, or specific concerns..."
                    rows={4}
                    value={requestSituation}
                    onChange={(e) => setRequestSituation(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Request Priority</Label>
                    <Select value={requestPriority} onValueChange={setRequestPriority}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low - No rush</SelectItem>
                        <SelectItem value="medium">Medium - Within 2 weeks</SelectItem>
                        <SelectItem value="high">High - Within 1 week</SelectItem>
                        <SelectItem value="urgent">Urgent - ASAP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t">
                  <h4 className="text-sm font-semibold">Portfolio Context (Optional)</h4>
                  <div className="space-y-2">
                    <Label>Total AUM / Portfolio Value</Label>
                    <Input 
                      placeholder="e.g., $850,000" 
                      value={totalValue}
                      onChange={(e) => setTotalValue(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Top Holdings (Optional)</Label>
                    <Input 
                      placeholder="e.g., AAPL, MSFT, NVDA (comma separated)" 
                      value={topHoldings}
                      onChange={(e) => setTopHoldings(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Key Concerns (Optional)</Label>
                    <Textarea 
                      placeholder="e.g., Concentration risk, lack of international exposure" 
                      rows={2}
                      value={concerns}
                      onChange={(e) => setConcerns(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t">
                  <h4 className="text-sm font-semibold">Fee Structure</h4>
                  <div className="space-y-2">
                    <Label>Preferred Fee Structure</Label>
                    <Select 
                      key="research-fee-structure-select"
                      value={researchFeeType}
                      onValueChange={setResearchFeeType}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select preference" />
                      </SelectTrigger>
                      <SelectContent>
                        {ANALYST_FEE_TYPES.map((fee) => (
                          <SelectItem key={fee.value} value={fee.value}>
                            <div className="flex flex-col">
                              <span className="font-medium">{fee.label}</span>
                              <span className="text-[10px] text-muted-foreground line-clamp-1">
                                {fee.description}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {researchFeeType && 
                  researchFeeType !== "flexible" && 
                  researchFeeType !== "engagement_agreement" && (
                   <div className="grid grid-cols-2 gap-4">
                     <div className="space-y-2">
                       <Label>Proposed Fee Amount ($)</Label>
                       <Input 
                         type="number" 
                         placeholder="e.g. 500" 
                         value={researchFeeAmount}
                         onChange={(e) => setResearchFeeAmount(e.target.value)}
                       />
                     </div>
                     <div className="space-y-2">
                        <Label>Payment Term</Label>
                        <Select 
                          value={researchPaymentTerm}
                          onValueChange={setResearchPaymentTerm}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select term" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="UPFRONT">Upfront</SelectItem>
                            <SelectItem value="MONTHLY">Monthly</SelectItem>
                            <SelectItem value="QUARTERLY">Quarterly</SelectItem>
                            <SelectItem value="ANNUALLY">Annually</SelectItem>
                            <SelectItem value="ON_COMPLETION">On Completion</SelectItem>
                            <SelectItem value="MILESTONE_BASED">Milestone-based</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                   </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Primary Goals/Focus Areas (Select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {FINANCIAL_SPECIALIZATIONS.map((goal) => (
                      <div key={`research-request-goal-${goal}`} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`research-request-goal-${goal}`} 
                          checked={selectedGoals.includes(goal)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedGoals([...selectedGoals, goal]);
                            } else {
                              setSelectedGoals(selectedGoals.filter(g => g !== goal));
                            }
                          }}
                        />
                        <label htmlFor={`research-request-goal-${goal}`} className="text-sm cursor-pointer">
                          {goal}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Specific Questions (Optional)</Label>
                  <Textarea
                    placeholder="List any specific questions you'd like answered (one per line)..."
                    rows={3}
                    value={questions}
                    onChange={(e) => setQuestions(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Expected Deliverables (Optional)</Label>
                  <Textarea
                    placeholder="List expected deliverables (e.g., PDF report, Excel model)..."
                    rows={3}
                    value={deliverables}
                    onChange={(e) => setDeliverables(e.target.value)}
                  />
                </div>

                <div className="space-y-3 py-2 border-t">
                  {(userRole === 'investor' || authRole === 'INVESTOR') && (
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="share-portfolio-research-request" 
                        checked={sharePortfolio}
                        onCheckedChange={(checked) => setSharePortfolio(!!checked)}
                      />
                      <Label htmlFor="share-portfolio-research-request" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        Share Portfolio
                      </Label>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="share-profile-research-request" 
                      checked={shareProfile}
                      onCheckedChange={(checked) => setShareProfile(!!checked)}
                    />
                    <Label htmlFor="share-profile-research-request" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Share Profile details
                    </Label>
                  </div>
                </div>

                <FileUploadSection 
                  id="research-request-upload"
                  helpText="Attach portfolio statements, financial documents, or any relevant files to help the analyst understand your request." 
                  onFilesChange={(files) => setUploadedFiles(files)}
                />

                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium mb-1">What happens next?</p>
                      <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                        <li>Analyst receives your request and reviews it</li>
                        <li>They may ask clarifying questions</li>
                        <li>Once accepted, they'll provide timeline and deliverables</li>
                        <li>You'll receive the completed research directly</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsRequestDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateRequest}>
                  <Send className="mr-2 h-4 w-4" />
                  Submit Request
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {userRole === "analyst" ? "Total Requests" : "My Requests"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{filteredRequests.length}</div>
            <p className="text-xs text-muted-foreground">
              {userRole === "analyst" ? "From all clients" : "Submitted"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{pendingCount}</div>
            <p className="text-xs text-muted-foreground">Awaiting response</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{activeCount}</div>
            <p className="text-xs text-muted-foreground">In progress</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{completedCount}</div>
            <p className="text-xs text-muted-foreground">Delivered</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search requests..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Requests List and Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Requests List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Requests</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {isFetchingRequests ? (
                <div className="text-center py-12">
                  <Clock className="h-10 w-10 mx-auto mb-4 animate-spin text-muted-foreground" />
                  <p className="text-muted-foreground">Loading requests...</p>
                </div>
              ) : filteredRequests.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No requests found</p>
                </div>
              ) : (
                filteredRequests.map((request) => {
                  const Icon = getRequestTypeIcon(request.requestType);

                  return (
                    <div
                      key={request.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-all hover:bg-muted/50 ${
                        selectedRequest?.id === request.id ? "bg-muted border-primary" : ""
                      }`}
                      onClick={() => setSelectedRequest(request)}
                    >
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Icon className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium line-clamp-2 text-sm mb-1">
                              {request.title}
                            </h4>
                            <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                              {request.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={getStatusColor(request.status)} variant="secondary">
                            {request.status.replace("_", " ")}
                          </Badge>
                          {request.priority !== "low" && (
                            <Badge className={getPriorityColor(request.priority)} variant="secondary">
                              {request.priority}
                            </Badge>
                          )}
                        </div>

                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {request.requestedDate}
                          </div>
                          {request.messages && request.messages.length > 0 && (
                            <div className="flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              {request.messages.length}
                            </div>
                          )}
                        </div>

                        {userRole === "analyst" && (
                          <div className="flex items-center gap-2 pt-2 border-t">
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-xs">
                                {request.requester.name
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">
                                {request.requester.name}
                              </p>
                              <Badge
                                variant="outline"
                                className={`text-xs ${
                                  request.requester.role === "investor"
                                    ? "text-blue-700 border-blue-300 dark:text-blue-300"
                                    : "text-green-700 border-green-300 dark:text-green-300"
                                }`}
                              >
                                {request.requester.role}
                              </Badge>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>

        {/* Request Detail */}
        <div className="lg:col-span-2">
          {selectedRequest ? (
            <Tabs defaultValue="details" className="space-y-4">
              <TabsList>
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="discussion">
                  Discussion
                  {selectedRequest.messages && selectedRequest.messages.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {selectedRequest.messages.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Details Tab */}
              <TabsContent value="details" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getStatusColor(selectedRequest.status)}>
                            {selectedRequest.status.replace("_", " ")}
                          </Badge>
                          <Badge className={getPriorityColor(selectedRequest.priority)}>
                            {selectedRequest.priority} priority
                          </Badge>
                          <Badge variant="outline">
                            {getRequestTypeLabel(selectedRequest.requestType)}
                          </Badge>
                        </div>
                        <CardTitle className="mb-2">{selectedRequest.title}</CardTitle>
                        <p className="text-sm text-muted-foreground">
                          {selectedRequest.description}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Requester/Analyst Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">
                          {userRole === "analyst" ? "Requester" : "Recipient"}
                        </Label>
                        <div className="flex items-center gap-3 mt-2">
                          <Avatar>
                            <AvatarFallback>
                              {(userRole === "analyst"
                                ? selectedRequest.requester.name
                                : selectedRequest.analyst?.name || "?"
                              )
                                ?.split(" ")
                                .filter(Boolean)
                                .map((n) => n[0])
                                .join("") || "?"}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">
                              {userRole === "analyst"
                                ? selectedRequest.requester.name
                                : selectedRequest.analyst?.name || "Not assigned"}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {userRole === "analyst"
                                ? selectedRequest.requester.firm || selectedRequest.requester.role
                                : selectedRequest.analyst?.firm}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Timeline</Label>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span>Requested: {selectedRequest.requestedDate}</span>
                          </div>
                          {selectedRequest.dueDate && (
                            <div className="flex items-center gap-2 text-sm">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <span>Due: {selectedRequest.dueDate}</span>
                            </div>
                          )}
                          {selectedRequest.completedDate && (
                            <div className="flex items-center gap-2 text-sm text-green-600">
                              <CheckCircle2 className="h-4 w-4" />
                              <span>Completed: {selectedRequest.completedDate}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Context */}
                    <div>
                      <Label className="mb-2">Context & Background</Label>
                      <p className="text-sm text-muted-foreground">{selectedRequest.context}</p>
                    </div>

                    {/* Focus Areas */}
                    <div>
                      <Label className="text-xs text-muted-foreground">Focus Areas</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {Array.isArray(selectedRequest.focusAreas) && selectedRequest.focusAreas.map((area, idx) => (
                          <Badge key={idx} variant="outline">
                            {area}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Specific Questions */}
                    {Array.isArray(selectedRequest.specificQuestions) &&
                      selectedRequest.specificQuestions.length > 0 && (
                        <div>
                          <Label className="mb-2">Specific Questions</Label>
                          <ul className="space-y-2">
                            {selectedRequest.specificQuestions.map((question, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm">
                                <span className="text-primary mt-1">•</span>
                                <span>{question}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                    {/* Portfolio Context */}
                    {selectedRequest.portfolioContext && (
                      <div>
                        <Label className="mb-2">Portfolio Context</Label>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          {selectedRequest.portfolioContext.totalValue && (
                            <div>
                              <span className="text-muted-foreground">Total Value:</span>
                              <p className="font-medium">
                                {selectedRequest.portfolioContext.totalValue}
                              </p>
                            </div>
                          )}
                          {selectedRequest.portfolioContext.topHoldings && Array.isArray(selectedRequest.portfolioContext.topHoldings) && (
                            <div>
                              <span className="text-muted-foreground">Top Holdings:</span>
                              <p className="font-medium">
                                {selectedRequest.portfolioContext.topHoldings.join(", ")}
                              </p>
                            </div>
                          )}
                        </div>
                        {Array.isArray(selectedRequest.portfolioContext.concerns) && (
                          <div className="mt-3">
                            <span className="text-muted-foreground text-sm">Concerns:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {selectedRequest.portfolioContext.concerns.map((concern, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  {concern}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Deliverables */}
                    {Array.isArray(selectedRequest.deliverables) && selectedRequest.deliverables.length > 0 && (
                      <div>
                        <Label className="mb-2">Expected Deliverables</Label>
                        <ul className="space-y-2">
                          {selectedRequest.deliverables.map((deliverable, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                              <span>{deliverable}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Attachments */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Attachments</Label>
                        <Dialog open={isAdditionalUploadOpen} onOpenChange={setIsAdditionalUploadOpen}>
                          <DialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 text-xs">
                              <Upload className="h-3 w-3 mr-1" />
                              Upload Additional
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Upload Additional Files</DialogTitle>
                              <DialogDescription>
                                Add more documents or files to this research request.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="py-4">
                              <FileUploadSection 
                                id="additional-files-upload"
                                helpText="Upload any additional files relevant to this request." 
                                onFilesChange={(files) => handleUploadAdditionalFiles(files)}
                              />
                            </div>
                            <DialogFooter>
                              <p className="text-xs text-muted-foreground italic text-center w-full">
                                Files are uploaded immediately upon selection.
                              </p>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                      
                      {Array.isArray(selectedRequest.documents) && selectedRequest.documents.length > 0 ? (
                        <div className="grid grid-cols-1 gap-2">
                          {selectedRequest.documents.map((doc, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleDownload(doc.fileUrl)}
                              className="flex items-center justify-between p-2 border rounded-md hover:bg-muted/50 transition-colors group text-left w-full"
                            >
                              <div className="flex items-center gap-3 overflow-hidden">
                                <div className="p-1.5 bg-primary/10 rounded-md">
                                  <Paperclip className="h-3.5 w-3.5 text-primary" />
                                </div>
                                <div className="overflow-hidden">
                                  <p className="text-xs font-medium truncate" title={doc.documentName}>
                                    {doc.documentName}
                                  </p>
                                  {doc.fileSizeBytes && (
                                    <p className="text-[10px] text-muted-foreground">
                                      {(doc.fileSizeBytes / 1024).toFixed(1)} KB
                                    </p>
                                  )}
                                </div>
                              </div>
                              <Download className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground italic px-2">No attachments found.</p>
                      )}
                    </div>

                    {/* Budget */}
                    {selectedRequest.budget && (
                      <div className="p-4 bg-muted rounded-lg">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-5 w-5 text-green-600" />
                          <div>
                            <Label className="text-xs text-muted-foreground">Budget</Label>
                            <p className="font-medium">{selectedRequest.budget}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Research Content Section */}
                    {(userRole === "analyst" || selectedRequest.researchContent) && (
                      <div className="space-y-3 pt-4 border-t">
                        <Label className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Research Content / Recommendation
                        </Label>
                        {userRole === "analyst" && selectedRequest.status !== "completed" ? (
                          <div className="space-y-2">
                            <Textarea
                              placeholder="Add your research analysis and recommendations here before completing the request..."
                              value={researchContentState}
                              onChange={(e) => setResearchContentState(e.target.value)}
                              rows={6}
                            />
                            <div className="flex justify-end">
                              <Button 
                                size="sm" 
                                onClick={handleSaveResearchContent}
                                disabled={researchContentState === (selectedRequest.researchContent || "")}
                              >
                                Save Recommendation
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="p-4 bg-primary/5 rounded-lg border border-primary/10 whitespace-pre-wrap text-sm italic">
                            {selectedRequest.researchContent || "No research content provided yet."}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Upcoming Meetings */}
                    {upcomingMeetings.length > 0 && (
                      <div className="space-y-3 pt-4 border-t">
                        <Label className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-blue-600" />
                          Upcoming Meetings
                        </Label>
                        <div className="space-y-2">
                          {upcomingMeetings.map((meeting) => (
                            <div key={meeting.id || meeting.meetingId} className="p-3 bg-blue-50/50 border border-blue-100 rounded-lg text-xs">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-3.5 w-3.5 text-blue-600" />
                                  <div className="flex flex-col">
                                    <span className="font-semibold text-blue-900">
                                      {(() => {
                                        const dateStr = meeting.startTime.endsWith('Z') || meeting.startTime.includes('+') 
                                          ? meeting.startTime 
                                          : `${meeting.startTime}Z`;
                                        return new Date(dateStr).toLocaleString([], { 
                                          month: 'short', 
                                          day: 'numeric', 
                                          hour: 'numeric', 
                                          minute: '2-digit'
                                        });
                                      })()}
                                    </span>
                                    <span className="text-[10px] text-blue-700/70">{meeting.meetingTitle || "Research Discussion"}</span>
                                  </div>
                                </div>
                                <Badge 
                                  variant="outline" 
                                  className={cn(
                                    "text-[10px] px-1.5 py-0 h-4.5 font-medium border-0",
                                    meeting.status === "ACCEPTED" 
                                      ? "bg-green-100 text-green-700" 
                                      : "bg-yellow-100 text-yellow-700"
                                  )}
                                >
                                  {meeting.status}
                                </Badge>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex justify-end gap-2 pt-2 border-t border-blue-100/50">
                                {meeting.hostId === currentUserId ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleUpdateMeetingStatus(meeting.id || meeting.meetingId,meeting.participantId, "CANCELLED")}
                                  >
                                    <XCircle className="mr-1 h-3 w-3" />
                                    Cancel
                                  </Button>
                                ) : meeting.status === "PENDING" && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2 text-[10px] text-green-600 hover:text-green-700 hover:bg-green-50"
                                      onClick={() => handleUpdateMeetingStatus(meeting.id || meeting.meetingId, currentUserId, "ACCEPTED")}
                                    >
                                      <CheckCircle2 className="mr-1 h-3 w-3" />
                                      Accept
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => handleUpdateMeetingStatus(meeting.id || meeting.meetingId, currentUserId, "DECLINED")}
                                    >
                                      <XCircle className="mr-1 h-3 w-3" />
                                      Decline
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Schedule Meeting Button */}
                    <div className="pt-4 border-t">
                      <Button 
                        onClick={() => setIsScheduleDialogOpen(true)} 
                        className="w-full"
                        variant="outline"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        Schedule Meeting
                      </Button>
                    </div>

                    {/* Actions for Analyst */}
                    {userRole === "analyst" && selectedRequest.status === "pending" && (
                      <div className="flex gap-3 pt-4 border-t">
                        <Button
                          onClick={() => handleUpdateStatus(selectedRequest.id, "accepted")}
                          className="flex-1"
                        >
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          Accept Request
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleUpdateStatus(selectedRequest.id, "declined")}
                        >
                          <XCircle className="mr-2 h-4 w-4" />
                          Decline
                        </Button>
                      </div>
                    )}

                    {userRole === "analyst" && selectedRequest.status === "accepted" && (
                      <Button
                        onClick={() => handleUpdateStatus(selectedRequest.id, "in_progress")}
                        className="w-full"
                      >
                        <ArrowRight className="mr-2 h-4 w-4" />
                        Start Working
                      </Button>
                    )}

                    {userRole === "analyst" && selectedRequest.status === "in_progress" && (
                      <Button
                        onClick={() => handleUpdateStatus(selectedRequest.id, "completed")}
                        className="w-full"
                      >
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Mark as Completed
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Discussion Tab */}
              <TabsContent value="discussion" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      Discussion
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {selectedRequest.messages && selectedRequest.messages.length > 0 ? (
                      <>
                        <div className="space-y-4 max-h-[400px] overflow-y-auto">
                          {selectedRequest.messages.map((message) => {
                            const isCurrentUser =
                              (userRole === "analyst" && message.sender === "analyst") ||
                              (userRole !== "analyst" && message.sender === "requester");

                            return (
                              <div
                                key={message.id}
                                className={`flex gap-3 ${isCurrentUser ? "flex-row-reverse" : ""}`}
                              >
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className="text-xs">
                                    {message.senderName
                                      .split(" ")
                                      .map((n) => n[0])
                                      .join("")}
                                  </AvatarFallback>
                                </Avatar>
                                <div
                                  className={`flex-1 space-y-1 ${
                                    isCurrentUser ? "items-end" : ""
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">
                                      {message.senderName}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {message.timestamp}
                                    </span>
                                  </div>
                                  <div
                                    className={`p-3 rounded-lg text-sm ${
                                      isCurrentUser
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-muted"
                                    }`}
                                  >
                                    {message.content}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <Separator />
                      </>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No messages yet</p>
                        <p className="text-sm">Start the conversation below</p>
                      </div>
                    )}

                    {/* Message Input */}
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        rows={3}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.ctrlKey) {
                            handleSendMessage();
                          }
                        }}
                      />
                      <Button onClick={handleSendMessage} disabled={!newMessage.trim()}>
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Press Ctrl+Enter to send
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          ) : (
            <Card>
              <CardContent className="py-20 text-center text-muted-foreground">
                <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Select a request to view details</p>
                <p className="text-sm">Choose from the list to see full information</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <ScheduleMeetingDialog 
        professional={
          selectedRequest 
            ? userRole === "analyst" 
              ? { id: selectedRequest.requester.id, name: selectedRequest.requester.name }
              : { id: selectedRequest.analyst?.id || "", name: selectedRequest.analyst?.name || "" }
            : null
        }
        requestId={selectedRequest?.id}
        isOpen={isScheduleDialogOpen}
        onOpenChange={setIsScheduleDialogOpen}
      />
    </div>
  );
}
